import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    Chip,
    Paper,
    Link,
    Dialog,
    DialogTitle,
    DialogContent
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { ApiRequestFailed, authenticateAndFetchData, retryFn } from "@lib/apis";
import { useEffect, useState } from "react";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import { ArrowBack, Visibility, Download } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { urls } from "@routes";
import { SurveyCompletion, Survey } from "@admin/types/survey.types";
import IconButton from "@mui/material/IconButton";
import {useAuth} from "./AuthProvider";
import { getExportFilename } from "@lib/utils";

interface ResponsesPageData {
    status_code: number;
    status_text: string;
    completions: SurveyCompletion[];
    total_count: number;
    total_pages: number;
    current_page: number;
}

interface SurveysPageData {
    status_code: number;
    status_text: string;
    surveys: Survey[];
}

export default function AdminSurveyResponses() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    const [responsesData, setResponsesData] = useState<ResponsesPageData>();
    const [surveysData, setSurveysData] = useState<SurveysPageData>();
    const [viewDetailsDialogOpen, setViewDetailsDialogOpen] = useState(false);
    const [selectedCompletion, setSelectedCompletion] = useState<SurveyCompletion | null>(null);
    const [isExporting, setIsExporting] = useState(false);

    // Filter states
    const [selectedSurveyUid, setSelectedSurveyUid] = useState(searchParams.get("survey_uid") || "");
    const [userEmailFilter, setUserEmailFilter] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    // Query for fetching all surveys (for filter dropdown)
    const surveysQuery = useQuery({
        queryKey: ["surveysForResponseFilter"],
        queryFn: () => authenticateAndFetchData("/admin/surveys/"),
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
    });

    const surveyResponseExportQuery = useQuery({
        queryKey: ["adminSurveyResponseExportQuery", selectedSurveyUid, userEmailFilter],
        queryFn: () => {
            let url = `/admin/survey/responses/export/`;
            const params = new URLSearchParams();
            if (selectedSurveyUid) params.append("survey_uid", selectedSurveyUid);
            if (userEmailFilter) params.append("user_email", userEmailFilter);

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            return authenticateAndFetchData(url);
        },
        enabled: false, // Prevents query from running automatically on page load
        gcTime: 0,
        refetchOnWindowFocus: false,
        retry: retryFn,
    });

    useEffect(() => {
        if (surveysQuery.data) {
            setSurveysData(surveysQuery.data.data);
        }
    }, [surveysQuery.data]);

    // Query for fetching responses with filters
    const responsesQuery = useQuery({
        queryKey: ["surveyCompletions", selectedSurveyUid, userEmailFilter, page, pageSize],
        queryFn: () => {
            let url = `/admin/survey/responses/?page=${page}&page_size=${pageSize}`;
            if (selectedSurveyUid) {
                url += `&survey_uid=${selectedSurveyUid}`;
            }
            if (userEmailFilter) {
                url += `&user_email=${userEmailFilter}`;
            }
            return authenticateAndFetchData(url);
        },
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
    });

    const handleExportCSV = async () => {
        try {
            setIsExporting(true);

            const { data: response, isError } = await surveyResponseExportQuery.refetch();

            if (isError || !response) {
                throw new Error("Export failed");
            }

            // response is an AxiosResponse; response.data contains the Blob / binary content
            const fileBlob = response.data instanceof Blob ? response.data : new Blob([response.data]);
            
            const downloadUrl = window.URL.createObjectURL(fileBlob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = getExportFilename(user?.email ?? "unknown", "SurveyResponse");
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error("Failed to export responses:", error);
        } finally {
            setIsExporting(false);
        }
    };

    useEffect(() => {
        if (responsesQuery.data) {
            setResponsesData(responsesQuery.data.data);
        }
    }, [responsesQuery.data]);

    const handleViewDetails = (completion: SurveyCompletion) => {
        setSelectedCompletion(completion);
        setViewDetailsDialogOpen(true);
    };

    const columns: GridColDef<SurveyCompletion>[] = [
        {
            field: "survey_title",
            headerName: "Survey",
            flex: 1,
            minWidth: 200,
            align: "left",
            headerAlign: "center",
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    color="primary"
                    variant="outlined"
                />
            )
        },
        {
            field: "user_email",
            headerName: "User Email",
            flex: 1,
            minWidth: 200,
            align: "left",
            headerAlign: "center",
        },
        {
            field: "completed_on",
            headerName: "Completed On",
            width: 180,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Typography variant="body2">
                    {new Date(params.value).toLocaleString()}
                </Typography>
            )
        },
        {
            field: "credits_awarded",
            headerName: "Credits Status",
            width: 140,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Stack direction="column" alignItems="center" spacing={0.5}>
                    <Chip
                        label={params.value ? "Awarded" : "Pending"}
                        size="small"
                        color={params.value ? "success" : "warning"}
                        variant="outlined"
                    />
                    {params.row.credits_amount > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            {params.row.credits_amount} credits
                        </Typography>
                    )}
                </Stack>
            )
        },
        {
            field: "domain_awarded",
            headerName: "Domain Credit Status",
            width: 140,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Stack direction="column" alignItems="center" spacing={0.5}>
                    <Chip
                        label={params.value ? "Awarded" : "Pending"}
                        size="small"
                        color={params.value ? "success" : "warning"}
                        variant="outlined"
                    />
                    {params.row.domain_amount > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            {params.row.domain_amount} Domain
                        </Typography>
                    )}
                </Stack>
            )
        },
        {
            field: "attachment_url",
            headerName: "Attachment",
            width: 120,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => {
                // Check if any response has an attachment_url
                const responsesData = params.row.responses_data || {};
                const attachmentUrl = Object.values(responsesData).find(
                    (response: any) => response?.attachment_url
                )?.attachment_url;

                return attachmentUrl ? (
                    <Link href={attachmentUrl} target="_blank" rel="noopener noreferrer">
                        View File
                    </Link>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        None
                    </Typography>
                );
            }
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 100,
            filterable: false,
            sortable: false,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <IconButton
                    color="primary"
                    size="small"
                    onClick={() => handleViewDetails(params.row)}
                >
                    <Visibility fontSize="small" />
                </IconButton>
            )
        },
    ];

    if (responsesQuery.isLoading || surveysQuery.isLoading) {
        return <PageLoading />;
    }

    if (responsesQuery.isError) {
        return <PageDataErrorHandler error={responsesQuery.error as ApiRequestFailed} />;
    }

    return (
        <Stack direction="column" spacing={3} sx={{ pb: 4 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography fontWeight="bold" variant="h5">
                        Survey Responses
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        View and analyze survey completions from users
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1.5}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<Download />}
                        onClick={handleExportCSV}
                        disabled={isExporting}
                    >
                        {isExporting ? "Exporting..." : "Export CSV"}
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ArrowBack />}
                        onClick={() => navigate(urls["adminSurveys"])}
                    >
                        Back to Surveys
                    </Button>
                </Stack>
            </Stack>

            <Divider />

            {/* Filters */}
            <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    Filters
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                    <FormControl sx={{ minWidth: 250 }}>
                        <InputLabel>Filter by Survey</InputLabel>
                        <Select
                            value={selectedSurveyUid}
                            onChange={(e) => {
                                setSelectedSurveyUid(e.target.value);
                                setPage(1);
                            }}
                            label="Filter by Survey"
                            size="small"
                        >
                            <MenuItem value="">
                                <em>All Surveys</em>
                            </MenuItem>
                            {surveysData?.surveys.map((survey) => (
                                <MenuItem key={survey.uid} value={survey.uid}>
                                    {survey.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Filter by User Email"
                        value={userEmailFilter}
                        onChange={(e) => {
                            setUserEmailFilter(e.target.value);
                            setPage(1);
                        }}
                        size="small"
                        sx={{ minWidth: 250 }}
                    />
                </Stack>
            </Paper>

            {/* Stats */}
            {responsesData && (
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <Card sx={{ flexGrow: 1, minWidth: "200px" }}>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                Total Completions
                            </Typography>
                            <Typography variant="h4" sx={{ mt: 1 }}>
                                {responsesData.total_count}
                            </Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ flexGrow: 1, minWidth: "200px" }}>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                Current Page
                            </Typography>
                            <Typography variant="h4" sx={{ mt: 1 }}>
                                {responsesData.current_page} / {responsesData.total_pages}
                            </Typography>
                        </CardContent>
                    </Card>
                </Stack>
            )}

            {/* Data Table */}
            <Card>
                <CardContent>
                    <DataGrid
                        rows={responsesData?.completions || []}
                        columns={columns}
                        getRowId={(row) => `${row.user_email}-${row.survey_uid}`}
                        autoHeight
                        pageSizeOptions={[25, 50, 100]}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 50 },
                            },
                        }}
                        disableRowSelectionOnClick
                        sx={{
                            "& .MuiDataGrid-cell": {
                                display: "flex",
                                alignItems: "center",
                                py: 1,
                            },
                            "& .MuiDataGrid-cell:focus": {
                                outline: "none",
                            },
                        }}
                    />
                </CardContent>
            </Card>

            {/* View Details Dialog */}
            <Dialog
                open={viewDetailsDialogOpen}
                onClose={() => setViewDetailsDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Survey Response Details</DialogTitle>
                <DialogContent>
                    {selectedCompletion && (
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Survey
                                </Typography>
                                <Typography variant="body1">
                                    {selectedCompletion.survey_title}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    User
                                </Typography>
                                <Typography variant="body1">
                                    {selectedCompletion.user_email}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Completed On
                                </Typography>
                                <Typography variant="body1">
                                    {new Date(selectedCompletion.completed_on).toLocaleString()}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Responses
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: "auto" }}>
                                    <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
                                        {JSON.stringify(selectedCompletion.responses_data, null, 2)}
                                    </pre>
                                </Paper>
                            </Box>
                            {(() => {
                                // Extract attachment URLs from responses_data
                                const responsesData = selectedCompletion.responses_data || {};
                                const attachments = Object.entries(responsesData)
                                    .filter(([_, response]: [string, any]) => response?.attachment_url)
                                    .map(([questionId, response]: [string, any]) => ({
                                        questionId,
                                        url: response.attachment_url,
                                        expiresIn: response.attachment_expires_in
                                    }));

                                return attachments.length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Attachments
                                        </Typography>
                                        <Stack spacing={1}>
                                            {attachments.map((attachment, index) => (
                                                <Box key={attachment.questionId}>
                                                    <Link
                                                        href={attachment.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Attachment {index + 1}
                                                    </Link>
                                                    {attachment.expiresIn && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                            (expires in {Math.floor(attachment.expiresIn / 3600)} hours)
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>
                                );
                            })()}
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Credits
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                        label={selectedCompletion.credits_awarded ? "Awarded" : "Pending"}
                                        size="small"
                                        color={selectedCompletion.credits_awarded ? "success" : "warning"}
                                    />
                                    <Typography variant="body2">
                                        {selectedCompletion.credits_amount} credits
                                    </Typography>
                                </Stack>
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
            </Dialog>
        </Stack>
    );
}
